
import ViewModel from '@/models/ViewModel'
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator'
import IconAction from '@/components/IconAction/IconAction.vue'
import FormInput from '@/components/FormInput/FormInput.vue'
import Api from '@/models/Api'
import Inventory from '@/models/Inventory'
import WebMessage from '@/models/WebMessage'
import CloseTag from '@/components/CloseTag/CloseTag.vue'
import { InventoryTargetingNode } from '@/models/interface/TargetingNode'

@Component({
  components: {
    FormInput,
    IconAction,
    CloseTag,
  },
})
export default class InventoryTargetting extends ViewModel {
  @Prop()
  value!: any

  @Prop()
  exclude!: any

  @Prop()
  adserver!: string

  @Prop({ default: false })
  customInventory!: boolean

  @Prop()
  selectedMediaPackage!: any

  public selected_nodes: any = []

  public inventory_list: any = []

  public filter: any = ''

  public load_tracker: any = {}

  public loading: boolean = true

  public ready: boolean = false

  public backend_searching: boolean = false

  public mounted() {
    this.loadData()
  }

  // watch filter
  @Watch('filter')
  public onFilterChange(val: any) {
    if (val && val.length && !this.loading) {
      this.collapseAll()
      // search for this
      this.filterName(val)
    }
  }

  // top level inventory
  public get inventoryList() {
    let search_terms = this.filter.toLocaleLowerCase().split(' ')
    let ret = this.inventory_list.filter((i: any) => {
      if (search_terms.length > 0 && this.filter != '') {
        return search_terms.every((term: any) => i.name.toLocaleLowerCase().includes(term))
      }
      return !i.parent_id
    })
    return ret
  }

  public filterName(val: any) {
    Inventory.searchByName({ value: val }).then((res: any) => {
      this.backend_searching = true
      let ids: string[] = []
      let new_list: any = []

      let base = [...new Set([...this.inventory_list, ...res])]
      base.forEach((i: any) => {
        if (!ids.includes(i.id)) {
          ids.push(i.id)
          new_list.push(i)
        }
      })

      Vue.set(
        this,
        'inventory_list',
        new_list.sort((a: any, b: any) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        }),
      )
      res.forEach((i: any) => {
        Vue.set(this.load_tracker, i.id, this.setDemandObj(i.id))
      })
      this.backend_searching = false
    })
  }

  public confirmIfNeeded(exe: any) {
    if (!this.customInventory && this.selectedMediaPackage && this.selectedMediaPackage.length) {
      // alert user
      WebMessage.doubleConfirm(
        'Modify Original Inventory',
        'Are you sure you want to modify the Media Package original inventory?',
        'Yes, Change Media Package',
      ).then((res: any) => {
        if (res) {
          exe()
          this.$emit('update:customInventory', true)
        }
      })
    } else {
      exe()
    }
  }

  public select(node: InventoryTargetingNode, include: boolean = true) {
    let index = this.selected_nodes.findIndex((s: any) => s.id === node.id)
    let _action = include ? 'include' : 'exclude'
    let obj: InventoryTargetingNode = {
      ...node,
      action: _action,
    }

    if (index > -1) {
      Vue.set(this.selected_nodes[index], 'action', _action)
      this.updatedModel(this.selected_nodes[index])
    } else {
      this.selected_nodes.push(obj)
      this.updatedModel(obj)
    }

    this.emit('select')
  }

  public removeAll() {
    this.selected_nodes = []
    this.emit('removeAll')
    this.updatedModel(null, true)
  }

  public remove(index: number) {
    this.updatedModel({ ...this.selected_nodes[index], action: 'remove' })
    setTimeout(() => {
      this.selected_nodes.splice(index, 1)
      this.emit('remove')
    }, 100)
  }

  public isSelected(node: InventoryTargetingNode) {
    return this.selected_nodes.some(sn => sn.name === node.name)
  }

  public isIncluded(node: InventoryTargetingNode) {
    return this.selected_nodes.some(sn => sn.id === node.id && sn.action === 'include')
  }

  public isExcluded(node: InventoryTargetingNode) {
    return this.selected_nodes.some(sn => sn.id === node.id && sn.action === 'exclude')
  }

  public emit(from: string) {
    setTimeout(() => {
      let include = this.selected_nodes.filter(sn => sn.action === 'include')
      let exclude = this.selected_nodes.filter(sn => sn.action === 'exclude')

      const map_values = (arr: any) => arr.map((n: any) => n.id)

      this.$emit('input', {
        ...this.value,
        include_adunits: map_values(include),
        exclude_adunits: map_values(exclude),
      })
    }, 400)
  }

  public setDemandObj(id: string) {
    return {
      loading: true,
      open_child: false,
      childs: null, // [ids] later use these ids to remove all loaded childs from all parents
    }
  }

  public collapseAll() {
    let updated_obj: any = {}
    for (let i in this.load_tracker) {
      updated_obj[i] = {
        ...this.load_tracker[i],
        open_child: false,
      }
    }

    Vue.set(this, 'load_tracker', updated_obj)
  }

  public sortInventory(list: any[]) {
    return list.sort((a: any, b: any) => {
      if (a.name < b.name) return -1
      if (a.name > b.name) return 1
      return 0
    })
  }

  public async loadData() {
    try {
      if (this.ready) return

      let response = await Inventory.loadInventories({
        id: null,
        selected_inventory_ids: [...this.value.include_adunits, ...this.value.exclude_adunits],
      })

      this.inventory_list = this.sortInventory(response.inventories)

      await this.loadAdUnits(response)

      this.inventory_list.forEach((i: any) => {
        Vue.set(this.load_tracker, i.id, this.setDemandObj(i.id))
      })

      this.loading = false

      this.ready = true
    } catch (e) {
      this.loading = false
      this.ready = true
    }
  }

  public async loadAdUnits(inventories: any) {
    let promises = await inventories.selected_inventories.map(async (i: any) => ({
      ...i,
      action: this.value.include_adunits.includes(i.id) ? 'include' : 'exclude',
    }))

    let res = await Promise.all(promises)

    this.selected_nodes = res

    return true
  }

  public open_child(inventory_id: any) {
    this.$root.$emit('bv::toggle::collapse', `child-${inventory_id}`)
    // load child

    const load = () => {
      Inventory.loadInventories({
        id: inventory_id,
      }).then(inventories => {
        if (inventories.inventories.length) {
          this.inventory_list = [
            ...new Set([...this.inventory_list, ...inventories.inventories]),
          ].sort((a: any, b: any) => {
            if (a.name < b.name) {
              return -1
            }
            if (a.name > b.name) {
              return 1
            }
            return 0
          })
          Vue.set(this.load_tracker[inventory_id], 'childs', [
            ...new Set(inventories.inventories.map((i: any) => i.id)),
          ])
        }
        setTimeout(() => {
          Vue.set(this.load_tracker[inventory_id], 'loading', false)
          Vue.set(this.load_tracker[inventory_id], 'open_child', true)
        }, 200)
      })
    }

    if (this.load_tracker.hasOwnProperty(inventory_id)) {
      // check if childs are loaded
      if (this.load_tracker[inventory_id].childs && this.load_tracker[inventory_id].childs.length) {
        let state = this.load_tracker[inventory_id].open_child
        Vue.set(this.load_tracker[inventory_id], 'open_child', !state)
      } else {
        load()
      }
    } else {
      Vue.set(this.load_tracker, inventory_id, this.setDemandObj(inventory_id))
      load()
    }
  }

  public get_child(inventory_id: any) {
    let childs = Array.from(
      this.inventory_list
        .reduce((uniqueItems, item) => uniqueItems.set(item.name, item), new Map())
        .values(),
    )

    return childs.filter((i: any) => i.parent_id === inventory_id && i.id !== inventory_id)
  }

  public removeSelectionById(id: string) {
    const index = this.selected_nodes.findIndex((s: any) => s.id === id)
    if (index > -1) {
      this.remove(index)
      return true
    }
    return false
  }

  public updatedModel(node: InventoryTargetingNode | null, sync = false) {
    if (!node) {
      this.$emit('updated-model', { sync })
      return
    }
    node.origin = 'inventory'
    if (!this.ready) return
    setTimeout(() => {
      this.$emit('updated-model', Object.assign(new InventoryTargetingNode(), node))
    }, 100)
  }
}
